import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { Events, Link as ScrollLink } from 'react-scroll'

import classNames from 'classnames'
import classnames from 'classnames'

import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import Lozenge from '~/components/atoms/Lozenge/Lozenge'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { useInPageNavContext } from '~/contexts/InPageNavContext'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import { multiPostHogEventPusher } from '~/utils/analyticsPusher'
import { useScrollPosition } from '~/utils/useScrollPosition'

import styles from './InPageNav.module.css'

export interface InPageNavProps {
  items: {
    slug: string
    title: string
  }[]
  title?: string | ReactNode
  testID?: string
  center?: boolean
  bordered?: boolean
  idAppend?: string
  offwhite?: boolean
  mobileLozenge?: boolean
  activeTabClickHandler?: (slug: string) => void
  activeTab?: string
  noFix?: boolean
  footerComponent?: React.ReactNode
  noMarginTop?: boolean
}

export default function InPageNav({
  items,
  title,
  testID,
  center = false,
  bordered = false,
  idAppend = 'container',
  offwhite,
  mobileLozenge,
  activeTabClickHandler,
  activeTab,
  noFix = false,
  footerComponent,
  noMarginTop = false,
}: InPageNavProps) {
  const { mainHeaderVisible } = useInPageNavContext()
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMedium})`)
  const { setClickedInPageNav } = useInPageNavContext()
  const ref = useRef<any>(null)
  const elementRef: any = useRef(null)
  const [inViewRef, inView] = useInView({
    rootMargin: '-56px',
    initialInView: true,
  })

  const [isNotSticky, setIsNotSticky] = useState(true)
  const [initialPosition, setInitialPosition] = useState<number | null>(null)

  Events.scrollEvent.register('end', function () {
    setClickedInPageNav(false)
  })

  const setRefs = useCallback(
    (node: any) => {
      ref.current = node
      inViewRef(node)
    },
    [inViewRef]
  )

  useEffect(() => {
    if (inView) {
      ref.current.scrollLeft = 0
    }
  }, [inView])

  useEffect(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop

    const position = elementRef?.current?.getBoundingClientRect().top
    const height = elementRef?.current?.getBoundingClientRect().height

    const offset = isMobile ? 0 : -56

    setInitialPosition(scrollTop + position + height - offset)
  }, [isMobile])

  useScrollPosition(
    ({ currPos }) => {
      if (initialPosition === null || currPos.y === 0) return

      setIsNotSticky(currPos.y > Number(`-${initialPosition}`))
    },
    [initialPosition]
  )

  const renderMainNavigation = (ref?: any) => {
    return (
      <>
        <div
          className={classnames(
            'in-page-nav', // Plain class name required to show/hide from root html element
            styles.base,
            {
              [styles.defaultBase]: !footerComponent,
              [styles.alternativeBase]: footerComponent,
              [styles.noMarginTop]: noMarginTop,
            },
            bordered && styles.bordered,
            offwhite && styles.offwhite,
            noFix && styles.noFix
          )}
          data-testid={testID}
          ref={setRefs}
        >
          <div className={styles.innerScrollable} ref={ref}>
            <Container gutters={gutterTypes.always} cssClass={styles.container}>
              <div
                className={classNames([
                  styles.pageNav,
                  center && styles.pageNavCenter,
                ])}
              >
                {!isNotSticky && title && (
                  <Text
                    size={TextSizes.primaryTitleMed}
                    className={styles.pageTitle}
                  >
                    {title}
                  </Text>
                )}

                <div className={styles.pageHeroLinks}>
                  {items?.map((item, i) => {
                    const itemRef = [item.slug]
                    if (idAppend !== '') itemRef.push(idAppend)

                    return mobileLozenge && isMobile ? (
                      <Lozenge
                        title={item.title}
                        hideOnZero
                        onClick={() =>
                          activeTabClickHandler &&
                          activeTabClickHandler(item.slug)
                        }
                        isActive={activeTab === item.slug}
                        key={`${item.slug}-${i}`}
                      />
                    ) : (
                      <ScrollLink
                        to={`${itemRef.join('-')}`}
                        href={`#${item.slug}`}
                        key={`${item.slug}-${i}`}
                        smooth={true}
                        className={classnames(styles.link)}
                        offset={-68}
                        onClick={async () => {
                          setClickedInPageNav(true)
                          console.log('goto', itemRef.join('-'))

                          await multiPostHogEventPusher([
                            {
                              eventType: `${item.title} link clicked`,
                            },
                          ])
                        }}
                      >
                        <div className={styles.linkInner}>
                          <Text size={TextSizes.primaryTitleXSml}>
                            {item.title}
                          </Text>
                        </div>
                      </ScrollLink>
                    )
                  })}
                </div>
              </div>
            </Container>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {renderMainNavigation(elementRef)}
      {footerComponent && (
        <div
          className={classNames(styles.fixedNavContainer, {
            [styles.isVisible]: !isNotSticky,
            [styles.pushDown]: !isNotSticky && mainHeaderVisible,
          })}
        >
          {renderMainNavigation()}
          <div
            className={classNames(styles.footerContainer, {
              [styles.isVisible]: !isNotSticky,
            })}
          >
            <Container>
              <div className={styles.footerComponent}>{footerComponent}</div>
            </Container>
          </div>
        </div>
      )}
    </>
  )
}
